import React, { useState, useEffect, useRef } from 'react';
import ContactModal from '../components/ContactModal';

const HERO_VIDEOS = [
  'https://videos.pexels.com/video-files/6390163/6390163-hd_1920_1080_25fps.mp4',
  'https://videos.pexels.com/video-files/9502504/9502504-hd_1366_720_24fps.mp4',
  'https://videos.pexels.com/video-files/9198424/9198424-hd_1920_1080_25fps.mp4',
  'https://videos.pexels.com/video-files/8979633/8979633-hd_1920_1080_30fps.mp4',
  'https://videos.pexels.com/video-files/5923404/5923404-hd_1920_1080_30fps.mp4',
  'https://videos.pexels.com/video-files/8731228/8731228-hd_1920_1080_25fps.mp4',
];
const STAGE_ATHLETES = [
  'https://images.pexels.com/photos/6777232/pexels-photo-6777232.jpeg?auto=compress&cs=tinysrgb&h=900&w=600',
  'https://images.pexels.com/photos/10350229/pexels-photo-10350229.jpeg?auto=compress&cs=tinysrgb&h=900&w=600',
  'https://images.pexels.com/photos/6076492/pexels-photo-6076492.jpeg?auto=compress&cs=tinysrgb&h=900&w=600',
];

const HERO_SCENES = [
  'Every stage in sport opens up the next phase of an athlete\'s journey.',
  'In the game. All in.',
  'The strongest athletes plan while they\'re still playing.',
  'A plan. A pathway. A purpose.',
  'The next chapter begins.',
  'Building something bigger.',
];

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  const [stagesVisible, setStagesVisible] = useState(false);
  const stagesRef = useRef<HTMLDivElement>(null);
  const [whoVisible, setWhoVisible] = useState(false);
  const whoRef = useRef<HTMLDivElement>(null);
  const [instVisible, setInstVisible] = useState(false);
  const instRef = useRef<HTMLDivElement>(null);
  const vid1Ref = useRef<HTMLVideoElement>(null);
  const vid2Ref = useRef<HTMLVideoElement>(null);
  const heroSlotRef = useRef<0 | 1>(0);
  const heroTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const v1 = vid1Ref.current;
    if (!v1) return;
    v1.src = HERO_VIDEOS[0];
    v1.play().catch(() => {});

    let nextIdx = 0;
    heroTimerRef.current = setInterval(() => { // 4s per clip
      nextIdx = (nextIdx + 1) % HERO_VIDEOS.length;
      const slot = heroSlotRef.current;
      const front = slot === 0 ? vid1Ref.current : vid2Ref.current;
      const back  = slot === 0 ? vid2Ref.current : vid1Ref.current;
      if (!front || !back) return;

      back.src = HERO_VIDEOS[nextIdx];
      back.style.opacity = '0';
      back.style.zIndex = '2';
      front.style.zIndex = '1';
      back.play().catch(() => {});

      requestAnimationFrame(() => requestAnimationFrame(() => {
        back.style.opacity = '1';
        front.style.opacity = '0';
      }));

      const captured = nextIdx;
      const capturedSlot = slot;
      setTimeout(() => {
        heroSlotRef.current = capturedSlot === 0 ? 1 : 0;
        setHeroIdx(captured);
      }, 900);
    }, 4000);

    return () => { if (heroTimerRef.current) clearInterval(heroTimerRef.current); };
  }, []);

  useEffect(() => {
    const el = stagesRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setStagesVisible(true); obs.disconnect(); }
    }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const el = whoRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setWhoVisible(true); obs.disconnect(); }
    }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const el = instRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInstVisible(true); obs.disconnect(); }
    }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div style={{ fontFamily: "'Barlow', sans-serif", background: '#fff', color: '#0d1b2a', overflowX: 'hidden' }}>

      {/* ── NAV ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(10,15,26,0.96)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 48px', height: 80,
        }}>
          <a href="/" style={{ textDecoration: 'none', flexShrink: 0 }}>
            <img
              src="/bthd-email-sig-300x80-transparent.png"
              alt="Bigger Than Hoop Dreams"
              className="nav-logo-img"
              style={{ height: 38, width: 'auto', filter: 'brightness(0) invert(1)' }}
            />
          </a>
          <div className="nav-status" style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)',
              color: 'rgba(255,255,255,0.75)', fontSize: 10, fontWeight: 800,
              padding: '6px 14px', borderRadius: 20, letterSpacing: 1,
              textTransform: 'uppercase', whiteSpace: 'nowrap',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#bf1e2e', display: 'inline-block' }} />
              Now on iOS &amp; Android
            </span>
          </div>
          <div className="nav-text-links" style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
            <a href="/" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, textDecoration: 'none', letterSpacing: 0.4 }}>Home</a>
            <a href="/about.html" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, textDecoration: 'none', letterSpacing: 0.4 }}>About</a>
            <a href="/programs" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, textDecoration: 'none', letterSpacing: 0.4 }}>Programs</a>
            <a href="/workshop" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, textDecoration: 'none', letterSpacing: 0.4 }}>Workshop</a>
            <a href="/early-pathways-program" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, textDecoration: 'none', letterSpacing: 0.4 }}>Early Pathways</a>
            <button onClick={() => setContactOpen(true)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, textDecoration: 'none', letterSpacing: 0.4, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>Contact</button>
            <a href="/admin/login" style={{
              background: '#bf1e2e', color: '#fff', borderRadius: 8,
              padding: '10px 20px', fontSize: 13, fontWeight: 700,
              textDecoration: 'none', letterSpacing: 0.3, whiteSpace: 'nowrap',
            }}>Access Portal →</a>
          </div>
          <button
            className="nav-hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              display: 'none', background: 'none', border: 'none',
              cursor: 'pointer', padding: '8px', flexDirection: 'column',
              gap: 5, alignItems: 'center', justifyContent: 'center',
            }}
          >
            <span style={{ display: 'block', width: 22, height: 2, background: menuOpen ? 'rgba(255,255,255,0.4)' : '#fff', borderRadius: 2, transition: 'all 0.2s', transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
            <span style={{ display: 'block', width: 22, height: 2, background: '#fff', borderRadius: 2, opacity: menuOpen ? 0 : 1, transition: 'all 0.2s' }} />
            <span style={{ display: 'block', width: 22, height: 2, background: menuOpen ? 'rgba(255,255,255,0.4)' : '#fff', borderRadius: 2, transition: 'all 0.2s', transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
          </button>
        </div>
        <div className="nav-beta-banner" style={{
          display: 'none',
          alignItems: 'center', justifyContent: 'center', gap: 8,
          background: 'rgba(255,255,255,0.04)', borderTop: '1px solid rgba(255,255,255,0.12)',
          padding: '7px 20px',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#bf1e2e', display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: 1, textTransform: 'uppercase' }}>
              Now on iOS &amp; Android
            </span>
          </span>
        </div>
        {menuOpen && (
          <div className="nav-mobile-menu" style={{
            borderTop: '1px solid rgba(255,255,255,0.07)',
            padding: '16px 24px 24px',
            display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            <a href="/" onClick={() => setMenuOpen(false)} style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, fontWeight: 600, textDecoration: 'none', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Home</a>
            <a href="/about.html" onClick={() => setMenuOpen(false)} style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, fontWeight: 600, textDecoration: 'none', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>About</a>
            <a href="/programs" onClick={() => setMenuOpen(false)} style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, fontWeight: 600, textDecoration: 'none', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Programs</a>
            <a href="/workshop" onClick={() => setMenuOpen(false)} style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, fontWeight: 600, textDecoration: 'none', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Workshop</a>
            <a href="/early-pathways-program" onClick={() => setMenuOpen(false)} style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, fontWeight: 600, textDecoration: 'none', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Early Pathways</a>
            <button onClick={() => { setMenuOpen(false); setContactOpen(true); }} style={{ background: 'none', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)', fontSize: 16, fontWeight: 600, textDecoration: 'none', padding: '12px 0', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', width: '100%' }}>Contact</button>
            <a href="/admin/login" style={{
              background: '#bf1e2e', color: '#fff', borderRadius: 8,
              padding: '14px 20px', fontSize: 15, fontWeight: 700,
              textDecoration: 'none', textAlign: 'center', marginTop: 8,
            }}>Access Portal →</a>
          </div>
        )}
      </nav>

      {/* ── HERO: video background ── */}
      <section style={{ position: 'relative', height: '100vh', overflow: 'hidden', background: '#050a14' }}>

        {/* Video slot A */}
        <video ref={vid1Ref} muted playsInline style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover', zIndex: 1, transition: 'opacity 0.9s ease',
        }} />
        {/* Video slot B */}
        <video ref={vid2Ref} muted playsInline style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover', zIndex: 1, opacity: 0, transition: 'opacity 0.9s ease',
        }} />

        {/* Dark gradient overlay for text readability */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 3,
          background: 'linear-gradient(180deg, rgba(5,10,20,0.55) 0%, rgba(5,10,20,0.78) 100%)',
        }} />

        {/* Hero copy */}
        <div style={{
          position: 'relative', zIndex: 4, height: '100%',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '120px 24px 80px', textAlign: 'center',
        }}>
          <div style={{ maxWidth: 860 }}>
            <h1 style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              fontWeight: 900, fontSize: 'clamp(52px, 10vw, 96px)',
              color: '#fff', lineHeight: 0.95, letterSpacing: -2,
              marginBottom: 16,
            }}>
              BIGGER THAN<br />
              <span style={{ color: '#bf1e2e' }}>HOOP DREAMS</span>
            </h1>
            <p style={{
              fontSize: 'clamp(14px, 1.6vw, 18px)',
              color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.55, margin: 0, fontWeight: 400,
              fontStyle: 'italic',
            }}>
              A future where every athlete has a plan, a pathway, and a purpose.
            </p>
          </div>
        </div>

        {/* Scene label + progress indicators */}
        <div style={{
          position: 'absolute', bottom: 32, left: 0, right: 0, zIndex: 4,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
        }}>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 2.5,
            color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase',
          }}>
            {HERO_SCENES[heroIdx]}
          </span>
          <div style={{ display: 'flex', gap: 5 }}>
            {HERO_VIDEOS.map((_, i) => (
              <div key={i} style={{
                height: 3, borderRadius: 2,
                width: i === heroIdx ? 24 : 5,
                background: i === heroIdx ? '#bf1e2e' : 'rgba(255,255,255,0.22)',
                transition: 'all 0.4s ease',
              }} />
            ))}
          </div>
        </div>
      </section>

      {/* ── THE PROBLEM: stats carousel ── */}
      <style>{`
        @keyframes statsScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes stageFade {
          0%   { opacity: 0; }
          8%   { opacity: 1; }
          28%  { opacity: 1; }
          36%  { opacity: 0; }
          100% { opacity: 0; }
        }
        @keyframes dotPulse {
          0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 0 rgba(191,30,46,0.4); }
          50%       { opacity: 0.7; transform: scale(1.3); box-shadow: 0 0 0 4px rgba(191,30,46,0); }
        }
        @keyframes cardFloat {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-8px); }
        }
        @keyframes phoneTilt {
          0%   { transform: perspective(900px) rotateY(0deg); }
          25%  { transform: perspective(900px) rotateY(-10deg); }
          50%  { transform: perspective(900px) rotateY(0deg); }
          75%  { transform: perspective(900px) rotateY(10deg); }
          100% { transform: perspective(900px) rotateY(0deg); }
        }
        .stage-athlete { animation: stageFade 18s ease-in-out infinite; }
        .stage-dot { animation: dotPulse 2.4s ease-in-out infinite; }
        .stats-track {
          animation: statsScroll 56s linear infinite;
          display: flex;
          width: max-content;
          gap: 20px;
          padding: 0 48px;
        }
        .stats-track:hover { animation-play-state: paused; }
      `}</style>
      <section style={{ background: '#0a0f1a', padding: '80px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(32px, 5vw, 56px)', color: '#fff', lineHeight: 1, letterSpacing: -1, marginBottom: 20 }}>
              Every stage in sports deserves a plan.
            </h2>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', maxWidth: 620, margin: '0 auto', lineHeight: 1.8 }}>
              Athletes spend years developing elite discipline, resilience, precision, performance under pressure. Most never build a framework to translate those qualities into what comes next, whether that's next year or ten years from now. The data confirms it.
            </p>
          </div>
        </div>
        <div style={{ overflow: 'hidden' }}>
          <div className="stats-track">
            {[
              { pct: '~80%', label: 'PREPARATION GAP', desc: 'of retired athletes are unprepared for life after sport', source: 'Athletes for Care / NCAA GOALS Study, 2020', color: '#bf1e2e' },
              { pct: '16%', label: 'FINANCIAL CRISIS', desc: 'of professional athletes declare bankruptcy within 12 years of retirement', source: 'NBER Working Paper, Carlson et al.', color: '#e88c0e' },
              { pct: '39%', label: 'MENTAL DISTRESS', desc: 'of elite athletes experience significant mental distress post-career', source: 'Gouttebarge et al., British Journal of Sports Medicine, 2019', color: '#6b46c1' },
              { pct: '44%', label: 'IDENTITY & PURPOSE', desc: 'of former NCAA athletes struggle to find purpose post-sport', source: 'NCAA research via Merging Vets & Players', color: '#0fa86d' },
              { pct: '4.8 yrs', label: 'AVG CAREER LENGTH', desc: 'the average professional sports career, then what?', source: 'Blended avg: NFL (3.3) · NBA (4.5) · MLB (5.6) · NHL (5.5), from respective league data', color: '#014c8d' },
              { pct: '57.3%', label: 'NEGATIVE TRANSITION', desc: 'of student-athletes said their transition from sport was a negative experience', source: 'Journal of Issues in Intercollegiate Athletics', color: '#d97706' },
              { pct: '~80%', label: 'PREPARATION GAP', desc: 'of retired athletes are unprepared for life after sport', source: 'Athletes for Care / NCAA GOALS Study, 2020', color: '#bf1e2e' },
              { pct: '16%', label: 'FINANCIAL CRISIS', desc: 'of professional athletes declare bankruptcy within 12 years of retirement', source: 'NBER Working Paper, Carlson et al.', color: '#e88c0e' },
              { pct: '39%', label: 'MENTAL DISTRESS', desc: 'of elite athletes experience significant mental distress post-career', source: 'Gouttebarge et al., British Journal of Sports Medicine, 2019', color: '#6b46c1' },
              { pct: '44%', label: 'IDENTITY & PURPOSE', desc: 'of former NCAA athletes struggle to find purpose post-sport', source: 'NCAA research via Merging Vets & Players', color: '#0fa86d' },
              { pct: '4.8 yrs', label: 'AVG CAREER LENGTH', desc: 'the average professional sports career, then what?', source: 'Blended avg: NFL (3.3) · NBA (4.5) · MLB (5.6) · NHL (5.5), from respective league data', color: '#014c8d' },
              { pct: '57.3%', label: 'NEGATIVE TRANSITION', desc: 'of student-athletes said their transition from sport was a negative experience', source: 'Journal of Issues in Intercollegiate Athletics', color: '#d97706' },
            ].map((s, i) => (
              <div key={i} style={{
                width: 280, flexShrink: 0,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderTop: '3px solid rgba(255,255,255,0.14)',
                borderRadius: 16, padding: '32px 24px',
                display: 'flex', flexDirection: 'column',
              }}>
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 52, color: '#f0f4ff', lineHeight: 1, letterSpacing: -2, marginBottom: 10 }}>{s.pct}</div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' as const, marginBottom: 8 }}>{s.label}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginBottom: 12, flex: 1 }}>{s.desc}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.22)', lineHeight: 1.5, fontStyle: 'italic' }}>{s.source}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE SOLUTION: transition line + 4 stages ── */}
      <section style={{ background: '#07090e', padding: '80px 48px', position: 'relative', overflow: 'hidden' }}>
        {/* Athlete crossfade background */}
        {STAGE_ATHLETES.map((url, i) => (
          <div
            key={i}
            className="stage-athlete"
            style={{
              position: 'absolute', inset: 0,
              backgroundImage: `url(${url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center 20%',
              opacity: 0,
              animationDelay: `${i * 6}s`,
              zIndex: 0,
            }}
          />
        ))}
        {/* Dark overlay over athletes */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'linear-gradient(135deg, rgba(7,9,14,0.88) 0%, rgba(7,9,14,0.78) 50%, rgba(7,9,14,0.88) 100%)',
        }} />

        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(32px, 5vw, 56px)', color: '#fff', lineHeight: 1, letterSpacing: -1 }}>
              A pathway built<br />
              <span style={{ color: '#bf1e2e' }}>for elite minds.</span>
            </h2>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', maxWidth: 540, margin: '16px auto 0', lineHeight: 1.65 }}>
              The same discipline that made you elite in your sport is your foundation at every stage.
            </p>
          </div>
          <div ref={stagesRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
            {[
              { num: '01', title: 'Athlete Assessment', desc: 'Assess and map your athletic strengths to every stage ahead.', bg: '#0d1b2a' },
              { num: '02', title: 'Skills and Pathways', desc: 'A personalized roadmap built on your strengths, vision, and your next path.', bg: '#0d1b2a' },
              { num: '03', title: 'Leadership Development', desc: 'Build the skills, mindset, and network for off-field excellence.', bg: '#bf1e2e' },
              { num: '04', title: 'Lasting Impact', desc: 'Mentorship, investment, community impact. Your lasting contribution.', bg: '#0d1b2a' },
            ].map((s, i) => (
              /* Outer: entrance fade+slide. Inner: continuous float. Separate wrappers avoid transform conflict. */
              <div key={i} style={{
                opacity: stagesVisible ? 1 : 0,
                transform: stagesVisible ? 'translateY(0)' : 'translateY(28px)',
                transition: `opacity 0.65s ease ${i * 0.15}s, transform 0.65s ease ${i * 0.15}s`,
              }}>
                <div style={{
                  animation: stagesVisible ? `cardFloat ${3.8 + i * 0.3}s ease-in-out ${0.8 + i * 0.2}s infinite` : 'none',
                  background: s.bg,
                  borderRadius: 16, padding: '32px 24px', position: 'relative',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}>
                  <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 48, color: 'rgba(255,255,255,0.07)', lineHeight: 1, marginBottom: 16 }}>{s.num}</div>
                  <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 22, color: '#fff', marginBottom: 10 }}>{s.title}</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>{s.desc}</div>
                  <div className="stage-dot" style={{ position: 'absolute', top: 24, right: 24, width: 8, height: 8, borderRadius: '50%', background: '#bf1e2e', animationDelay: `${i * 0.6}s` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR: moved after solution ── */}
      <section style={{ background: '#0a0f1a', padding: '80px 48px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: '#bf1e2e', textTransform: 'uppercase', marginBottom: 12 }}>Who It's For</div>
            <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(32px, 5vw, 56px)', color: '#fff', lineHeight: 1, letterSpacing: -1 }}>
              Built for every stage<br />
              <span style={{ color: 'rgba(255,255,255,0.35)' }}>of the athlete journey.</span>
            </h2>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.45)', maxWidth: 540, margin: '16px auto 0', lineHeight: 1.65 }}>
              Built for collegiate, amateur, and professional athletes at every stage of their career journey.
            </p>
          </div>
          <div ref={whoRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {[
              { icon: '◈', tier: 'Amateur and Collegiate', desc: 'Build your identity and development plan before the next chapter begins, not after.', color: '#014c8d' },
              { icon: '◉', tier: 'Professional', desc: 'Active athletes preparing for life beyond their current contract and career.', color: '#bf1e2e' },
              { icon: '✦', tier: 'Retired Athletes', desc: 'Your career shaped who you are. Now build on it. Rediscover purpose, develop leadership, and create lasting impact.', color: '#0fa86d' },
            ].map((a, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderTop: '3px solid rgba(255,255,255,0.14)',
                borderRadius: 16, padding: '28px 22px',
                display: 'flex', flexDirection: 'column', gap: 12,
                opacity: whoVisible ? 1 : 0,
                transform: whoVisible ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.6s ease ${i * 0.15}s, transform 0.6s ease ${i * 0.15}s`,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.16)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, color: 'rgba(255,255,255,0.75)', fontWeight: 700,
                }}>{a.icon}</div>
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 20, color: '#fff' }}>{a.tier}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOR INSTITUTIONS: new section, AD/program director language ── */}
      <section style={{ background: '#050a12', padding: '80px 48px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: '#bf1e2e', textTransform: 'uppercase', marginBottom: 12 }}>For Institutions</div>
            <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(32px, 5vw, 56px)', color: '#fff', lineHeight: 1, letterSpacing: -1 }}>
              Your athletes need a plan.<br />
              <span style={{ color: 'rgba(255,255,255,0.4)' }}>So does your program.</span>
            </h2>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', maxWidth: 580, margin: '16px auto 0', lineHeight: 1.8 }}>
              Athletic directors, coaches, and program leaders face growing pressure around athlete welfare beyond sport. BTHD gives your institution a structured, measurable platform to fulfill that duty of care with reporting that proves it.
            </p>
          </div>
          <div ref={instRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 44 }}>
            {[
              { icon: '◎', title: 'Duty of Care', desc: 'Document and demonstrate your institution\'s commitment to athlete welfare beyond sport.' },
              { icon: '◈', title: 'Measurable Outcomes', desc: 'Track assessment completion, stage progress, and readiness across your full roster.' },
              { icon: '◉', title: 'Institutional Reporting', desc: 'Progress reports, cohort-level insights, and athlete data scoped exclusively to your institution.' },
            ].map((c, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderTop: '3px solid rgba(255,255,255,0.14)',
                borderRadius: 16, padding: '28px 24px',
                opacity: instVisible ? 1 : 0,
                transform: instVisible ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.6s ease ${i * 0.15}s, transform 0.6s ease ${i * 0.15}s`,
              }}>
                <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.75)', marginBottom: 14, fontWeight: 700 }}>{c.icon}</div>
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 20, color: '#fff', marginBottom: 10 }}>{c.title}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.65 }}>{c.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <a
              href="https://cal.com/bigger-than-hoop-dreams"
              target="_blank" rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                background: '#bf1e2e', color: '#fff',
                borderRadius: 10, padding: '16px 40px',
                fontSize: 15, fontWeight: 700,
                textDecoration: 'none', letterSpacing: 0.3,
              }}
            >
              Book a Demo →
            </a>
          </div>
        </div>
      </section>

      {/* ── APP SCREENSHOTS: four stage phones ── */}
      <section style={{ background: '#050a12', padding: '80px 48px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(32px, 5vw, 56px)', color: '#fff', lineHeight: 1, letterSpacing: -1 }}>
              Full platform,<br />
              <span style={{ color: '#bf1e2e' }}>all four stages.</span>
            </h2>
          </div>
          <div className="stages-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, alignItems: 'flex-start' }}>
            {[
              { num: '01', title: 'Athlete Assessment', src: '/screenshots/07-identity-profile.jpg', color: '#014c8d' },
              { num: '02', title: 'Skills and Pathways', src: '/screenshots/08-transition-path.jpg', color: '#bf1e2e' },
              { num: '03', title: 'Leadership Development', src: '/screenshots/03-leadership.jpg', color: '#0fa86d' },
              { num: '04', title: 'Lasting Impact', src: '/screenshots/05-legacy.jpg', color: '#e88c0e' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                <div style={{
                  position: 'relative',
                  background: '#0d1117',
                  borderRadius: 36,
                  border: '2px solid rgba(255,255,255,0.1)',
                  padding: 6,
                  boxShadow: '0 32px 64px rgba(0,0,0,0.6)',
                  width: '100%', maxWidth: 220,
                  animation: `phoneTilt ${7 + i * 0.8}s ease-in-out ${i * 1.4}s infinite`,
                  willChange: 'transform',
                }}>
                  <div style={{
                    position: 'absolute', top: 10, left: '50%',
                    transform: 'translateX(-50%)',
                    width: 72, height: 22,
                    background: '#000', borderRadius: 12, zIndex: 2,
                  }} />
                  <img
                    src={s.src}
                    alt={s.title}
                    style={{
                      width: '100%', height: 'auto',
                      borderRadius: 30, display: 'block',
                    }}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 11, color: '#bf1e2e', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>{s.num}</div>
                  <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 16, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.3 }}>{s.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA: split panel ── */}
      <section style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ background: '#0a0f1a', padding: '72px 48px 64px', textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(36px, 6vw, 64px)', color: '#fff', lineHeight: 0.95, letterSpacing: -2, marginBottom: 16 }}>
            READY TO GO<br /><span style={{ color: '#bf1e2e' }}>BEYOND THE GAME?</span>
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          {/* Athlete panel */}
          <div style={{
            background: '#ffffff',
            padding: '80px 48px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
            textAlign: 'center',
            borderRight: '1px solid rgba(0,0,0,0.08)',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 3, color: '#bf1e2e', textTransform: 'uppercase' as const, marginBottom: 14 }}>For Athletes</div>
            <h3 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(32px, 4vw, 52px)', color: '#0a0f1a', lineHeight: 0.95, letterSpacing: -1, marginBottom: 14 }}>
              Start Your<br />Journey
            </h3>
            <p style={{ fontSize: 13, color: 'rgba(10,27,42,0.55)', marginBottom: 32, lineHeight: 1.65, maxWidth: 260 }}>
              Build your identity. Map your transition. Own what comes next.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <a href="https://apps.apple.com/app/id6761592236" target="_blank" rel="noopener noreferrer">
                <img src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83" alt="Download on the App Store" style={{ height: 44, width: 'auto' }} />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.bthd.app" target="_blank" rel="noopener noreferrer">
                <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" style={{ height: 58, width: 'auto' }} />
              </a>
            </div>
          </div>
          {/* Institution panel */}
          <div style={{
            background: '#ffffff',
            padding: '80px 48px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 3, color: '#bf1e2e', textTransform: 'uppercase' as const, marginBottom: 14 }}>For Institutions</div>
            <h3 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(32px, 4vw, 52px)', color: '#0a0f1a', lineHeight: 0.95, letterSpacing: -1, marginBottom: 14 }}>
              Bring BTHD to<br />Your Program
            </h3>
            <p style={{ fontSize: 13, color: 'rgba(10,27,42,0.55)', marginBottom: 32, lineHeight: 1.65, maxWidth: 260 }}>
              Turn athlete welfare into measurable outcomes.
            </p>
            <a
              href="https://cal.com/bigger-than-hoop-dreams"
              target="_blank" rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                background: '#bf1e2e', color: '#fff',
                borderRadius: 10, padding: '16px 44px',
                fontSize: 15, fontWeight: 700,
                textDecoration: 'none', letterSpacing: 0.3,
              }}
            >
              Book a Demo →
            </a>
          </div>
        </div>
        <div style={{ background: '#050a12', padding: '22px 48px', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <a href="/about.html" style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', textDecoration: 'underline', letterSpacing: 0.3 }}>
            Learn more about our mission and team →
          </a>
        </div>
      </section>

      {/* ── SUPPORTED BY ── */}
      <section style={{ background: '#0a0f1a', padding: '52px 48px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: 24 }}>
            Supported By
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 24 }}>
            <a href="https://innovationfactory.ca/" target="_blank" rel="noopener noreferrer">
              <div style={{ background: '#fff', borderRadius: 10, padding: '6px 12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="/innovation-factory-logo.png" alt="Innovation Factory" className="supported-logo" style={{ height: 64, width: 'auto', display: 'block' }} />
              </div>
            </a>
            <a href="https://www.acceleratorcentre.com/" target="_blank" rel="noopener noreferrer">
              <div style={{ background: '#fff', borderRadius: 10, padding: '6px 12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="/accelerator-centre.jpg" alt="The Accelerator Centre" className="supported-logo" style={{ height: 64, width: 'auto', display: 'block' }} />
              </div>
            </a>
            <a href="https://www.harrycares.org/" target="_blank" rel="noopener noreferrer">
              <img src="/hcf-logo-FULL-WITH-ACRONYM.png" alt="HCF" className="supported-logo" style={{ height: 64, width: 'auto', display: 'block' }} />
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER: badges instance 2 (small, footer context) ── */}
      <footer style={{ background: '#0a0f1a', padding: '32px 48px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="footer-grid" style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 24 }}>
          <div>
            <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 14, color: '#bf1e2e', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
              Bigger Than Hoop Dreams
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>
              © 2026 Bigger Than Hoop Dreams Inc. All Rights Reserved
            </div>
          </div>
          <div className="footer-links-col" style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <a href="/privacy.html" style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', textDecoration: 'underline' }}>Privacy Policy</a>
            <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: 11 }}>·</span>
            <a href="/terms.html" style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', textDecoration: 'underline' }}>Terms of Service</a>
            <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: 11 }}>·</span>
            <a href="/data-request" style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', textDecoration: 'underline' }}>Data Request</a>
          </div>
          <div className="footer-right-col" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <a href="https://ca.linkedin.com/company/bthd" target="_blank" rel="noopener noreferrer"
                style={{ color: 'rgba(255,255,255,0.35)', display: 'flex', transition: 'color 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
                aria-label="BTHD on LinkedIn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="https://www.instagram.com/biggerthanhoopdreams/" target="_blank" rel="noopener noreferrer"
                style={{ color: 'rgba(255,255,255,0.35)', display: 'flex', transition: 'color 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
                aria-label="BTHD on Instagram">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <a href="https://apps.apple.com/app/id6761592236" target="_blank" rel="noopener noreferrer">
                <img src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83" alt="Download on the App Store" style={{ height: 30, width: 'auto', opacity: 0.8 }} />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.bthd.app" target="_blank" rel="noopener noreferrer">
                <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" style={{ height: 40, width: 'auto', opacity: 0.8 }} />
              </a>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @media (max-width: 768px) {
          .nav-status { display: none !important; }
          .nav-text-links { display: none !important; }
          .nav-hamburger { display: flex !important; }
          .nav-logo-img { height: 30px !important; }
          nav > div:first-of-type { padding: 0 20px !important; height: 64px !important; }
          .nav-beta-banner { display: flex !important; }
          .footer-grid { grid-template-columns: 1fr !important; text-align: center; gap: 20px !important; }
          .footer-links-col { justify-content: center !important; }
          .footer-right-col { justify-content: center !important; }
          .supported-logo { height: 36px !important; }
          .stages-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
    </div>
  );
}
